// Typeahead airport picker. Wraps the design's .field-box so the visual
// matches the rest of the search form. Hits /v1/airports/autocomplete/?q=
// (which proxies to mercury's hub table) and renders a dropdown of matches.
//
// Keyboard: ↑ ↓ move the active item, Enter selects, Esc closes.
// Mouse: click-to-select.

import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { cacheAirport, type AirportInfo } from "../lib/airports";

interface Props {
  label: string;
  /** Currently-selected airport — provides both the IATA code and the
   *  city/name to render in the field-box body. */
  value: AirportInfo;
  /** Called whenever the user picks a suggestion. */
  onChange: (info: AirportInfo) => void;
  /** True when this field-box should render its open/active state. */
  isActive: boolean;
  /** Click handler for opening the box. */
  onActivate: () => void;
  /** Closes the popover (e.g. when the parent wants to dismiss it). */
  onClose: () => void;
}

interface Suggestion {
  code: string;
  city: string;
  name: string;
}

export function AirportPicker(props: Props) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset query when entering edit mode.
  useEffect(() => {
    if (props.isActive) {
      setQuery("");
      setSuggestions([]);
      setActiveIdx(0);
      // Focus next tick so React has rendered the input.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [props.isActive]);

  // Debounced autocomplete fetch.
  useEffect(() => {
    if (!props.isActive) return;
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    const tid = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.get<{ results: Suggestion[] }>(
          `/v1/airports/autocomplete/?q=${encodeURIComponent(q)}`,
        );
        if (!cancelled) {
          setSuggestions(data.results || []);
          setActiveIdx(0);
        }
      } catch {
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [query, props.isActive]);

  function pick(s: Suggestion) {
    const info: AirportInfo = { code: s.code, city: s.city, name: s.name };
    cacheAirport(info);
    props.onChange(info);
    props.onClose();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = suggestions[activeIdx];
      if (s) pick(s);
    } else if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
    }
  }

  return (
    <div
      className={`field-box${props.isActive ? " active" : ""}`}
      onClick={(e) => { e.stopPropagation(); if (!props.isActive) props.onActivate(); }}
    >
      <div className="label">{props.label}</div>
      {props.isActive ? (
        <input
          ref={inputRef}
          type="text"
          className="value-input"
          placeholder="City or airport"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          style={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em" }}
        />
      ) : (
        <div className="value">{props.value.city || props.value.code}</div>
      )}
      <div className="sub">
        {props.value.code} {props.value.name ? `· ${props.value.name}` : ""}
      </div>

      {props.isActive && (suggestions.length > 0 || loading || query.trim().length >= 2) && (
        <div className="airport-suggest" onMouseDown={(e) => e.preventDefault()}>
          {loading && suggestions.length === 0 && (
            <div className="airport-suggest-item" style={{ color: "var(--ink-3)", cursor: "default" }}>
              <span className="code">…</span>
              <div className="info"><b>Searching…</b><span>looking up "{query}"</span></div>
            </div>
          )}
          {!loading && suggestions.length === 0 && query.trim().length >= 2 && (
            <div className="airport-suggest-item" style={{ color: "var(--ink-3)", cursor: "default" }}>
              <span className="code">—</span>
              <div className="info"><b>No matches</b><span>try another spelling or IATA code</span></div>
            </div>
          )}
          {suggestions.map((s, i) => (
            <div
              key={s.code}
              className={`airport-suggest-item${i === activeIdx ? " active" : ""}`}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => pick(s)}
            >
              <span className="code">{s.code}</span>
              <div className="info">
                <b>{s.city || s.name}</b>
                <span>{s.name}{s.city && s.name !== s.city ? ` · ${s.city}` : ""}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
